import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FileText, CheckCircle2, Clock, AlertCircle, Loader2, Upload, Download } from 'lucide-react';

const API = axios.create({ baseURL: '/api/v1' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

type AgreementType = 'nda' | 'cpa' | 'annex_b' | 'cprsa';
type AgreementStatus = 'pending' | 'accepted' | 'update_available' | 'not_available';
type AcceptanceMethod = 'e_signature' | 'admin_upload';

interface AgreementSummary {
  type: AgreementType;
  currentVersion: string | null;
  acceptedVersion: string | null;
  acceptedAt: string | null;
  status: AgreementStatus;
  acceptanceMethod: AcceptanceMethod | null;
  /** Row id of the latest acceptance (admin artifact download). */
  acceptedAgreementId?: number | null;
  /** True when the executed snapshot (pdf_gcs_uri) is stored. */
  artifactAvailable?: boolean;
}

const TYPE_LABELS: Record<AgreementType, string> = {
  nda: 'Mutual NDA & Non-Circumvention',
  cpa: 'Channel Partner Agreement',
  cprsa: 'Revenue Share Agreement',
  annex_b: 'Annex B — Integration Schedule',
};
// Display order; the backend omits cprsa for non-Revenue-Share Partners, so
// only types present in the response are rendered.
const ORDER: AgreementType[] = ['nda', 'cpa', 'cprsa', 'annex_b'];

function badge(status: AgreementStatus) {
  switch (status) {
    case 'accepted': return { label: 'Accepted', cls: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 };
    case 'pending': return { label: 'Action needed', cls: 'bg-amber-100 text-amber-800', Icon: Clock };
    case 'update_available': return { label: 'New version', cls: 'bg-blue-100 text-blue-800', Icon: AlertCircle };
    default: return { label: 'Not available', cls: 'bg-slate-100 text-slate-600', Icon: Clock };
  }
}

export const PartnerAgreementsStatusCard: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [list, setList] = useState<AgreementSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingType, setUploadingType] = useState<AgreementType | null>(null);
  const [uploadError, setUploadError] = useState<Record<AgreementType, string | null>>({});
  const [artifactError, setArtifactError] = useState<string | null>(null);
  const fileInputs = useRef<Record<AgreementType, HTMLInputElement | null>>({});

  async function refresh() {
    try {
      const { data } = await API.get(`/disbursement-clients/${clientId}/agreements`);
      setList(data.agreements || []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load agreements');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get(`/disbursement-clients/${clientId}/agreements`);
        if (!cancelled) setList(data.agreements || []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load agreements');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  async function handleUpload(type: AgreementType, file: File) {
    setUploadingType(type);
    setUploadError((prev) => ({ ...prev, [type]: null }));
    try {
      const form = new FormData();
      form.append('document', file);
      await API.post(`/disbursement-clients/${clientId}/agreements/${type}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refresh();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const msg = axiosErr?.response?.data?.error || (err instanceof Error ? err.message : 'Upload failed');
      setUploadError((prev) => ({ ...prev, [type]: msg }));
    } finally {
      setUploadingType(null);
    }
  }

  // 2026-10-13: open the executed agreement artifact (the CP-signed snapshot)
  // via a short-TTL signed URL. MyMoolah countersigns offline — print the
  // opened document to PDF, sign, scan, and file it under
  // docs/legal/partners/<partner>/.
  async function downloadArtifact(agreementId: number) {
    setArtifactError(null);
    try {
      const { data } = await API.get(
        `/disbursement-clients/${clientId}/agreements/${agreementId}/artifact-url`
      );
      const url = data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else setArtifactError('Could not open the signed document');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setArtifactError(axiosErr?.response?.data?.error || (err instanceof Error ? err.message : 'Could not open the signed document'));
    }
  }

  return (
    <div className="mymoolah-card overflow-hidden">
      <div className="p-5 border-b border-gray-100">
        <h3 className="admin-text-heading text-lg">Agreements</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          In-portal e-signatures recorded by the Channel Partner, plus admin-uploaded signed PDFs (immutable acceptance record).
        </p>
      </div>
      <div className="p-5">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {artifactError && <p className="text-sm text-red-600">{artifactError}</p>}
        {!loading && !error && (
          <div className="space-y-3">
            {ORDER.filter((type) => (list ?? []).some((a) => a.type === type)).map((type) => {
              const item = list?.find((a) => a.type === type);
              const status = item?.status ?? 'not_available';
              const b = badge(status);
              const Icon = b.Icon;
              const isUploading = uploadingType === type;
              const err = uploadError[type];
              const methodLabel = item?.acceptanceMethod === 'admin_upload'
                ? 'Uploaded by admin'
                : item?.acceptanceMethod === 'e_signature'
                  ? 'E-signed by partner'
                  : null;
              return (
                <div key={type} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 p-3">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-gray-100 p-2">
                      <FileText className="h-4 w-4 text-gray-500" aria-hidden />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{TYPE_LABELS[type]}</p>
                      <p className="text-xs text-gray-400">
                        {item?.acceptedVersion
                          ? `Accepted v${item.acceptedVersion}${item.acceptedAt ? ` on ${new Date(item.acceptedAt).toLocaleDateString()}` : ''}${methodLabel ? ` · ${methodLabel}` : ''}`
                          : item?.currentVersion
                            ? `Current version v${item.currentVersion} — not yet accepted`
                            : 'Template not yet published'}
                      </p>
                      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${b.cls}`}>
                      <Icon className="h-3.5 w-3.5" aria-hidden /> {b.label}
                    </span>
                    {status === 'accepted' && item?.artifactAvailable && item?.acceptedAgreementId && (
                      <button
                        type="button"
                        onClick={() => downloadArtifact(item.acceptedAgreementId as number)}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                        title="Open the executed (partner-signed) document — print to PDF for MyMoolah countersigning"
                      >
                        <Download className="h-3.5 w-3.5" aria-hidden /> Download signed copy
                      </button>
                    )}
                    <label
                      className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 cursor-pointer disabled:opacity-50"
                      title="Upload a paper-signed PDF on the Channel Partner's behalf"
                    >
                      {isUploading
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                        : <Upload className="h-3.5 w-3.5" aria-hidden />}
                      {isUploading ? 'Uploading…' : 'Upload signed PDF'}
                      <input
                        ref={(el) => { fileInputs.current[type] = el; }}
                        type="file"
                        accept="application/pdf,.pdf"
                        className="hidden"
                        disabled={isUploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleUpload(type, f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
